import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WarningIcon from "@mui/icons-material/Warning";
import { Alert, Box, Button, Card, IconButton, MenuItem, Select, Switch, TextField, Typography } from "@mui/material";
import { alphaVar, colors } from "../../../../ui/colors";
import { invoiceEditorSectionCardSx } from "../../../../ui/invoiceEditorSectionSurface";
import { EmailRecipient } from "../types";

interface EmailRecipientsListProps {
  recipients: EmailRecipient[];
  sendEmail: boolean;
  needsPaymentMethod?: boolean;
  needsCanPay?: boolean;
  allowCanPay?: boolean;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, field: keyof EmailRecipient, value: any) => void;
  onSendEmailChange: (value: boolean) => void;
}

export const EmailRecipientsList = ({
  recipients,
  sendEmail,
  needsPaymentMethod = false,
  needsCanPay = false,
  allowCanPay = true,
  onAdd,
  onDelete,
  onUpdate,
  onSendEmailChange,
}: EmailRecipientsListProps) => {
  const hasCanPay = recipients.some((recipient) => recipient.permissionLevel === "Can Pay");
  const isPaymentMethodRequired = needsPaymentMethod || (needsCanPay && hasCanPay);
  return (
    <Card
      elevation={0}
      sx={{
        ...invoiceEditorSectionCardSx,
        position: "relative",
      }}
    >
      {/* Switch - Always visible and interactive */}
      <Box
        sx={{
          p: 2.5,
          borderBottom: `1px solid ${alphaVar(colors.gray[200], 0.3)}`,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          position: "relative",
          zIndex: 20,
          pointerEvents: "auto",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: colors.gray[700], fontSize: "0.75rem" }}>
            Send invoice by email
          </Typography>
          <Switch
            checked={sendEmail}
            onChange={(e) => {
              e.stopPropagation();
              const newValue = e.target.checked;
              console.log("Switch onChange triggered:", newValue);
              onSendEmailChange(newValue);
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log("Switch onClick triggered");
            }}
            color="primary"
          />
        </Box>
      </Box>

      {/* Content with opacity when disabled */}
      <Box
        sx={{
          opacity: sendEmail ? 1 : 0.5,
          pointerEvents: sendEmail ? "auto" : "none",
          position: "relative",
          "&::before": sendEmail
            ? {}
            : {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: alphaVar(colors.gray[900], 0.02),
                zIndex: 1,
                cursor: "not-allowed",
              },
        }}
      >
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${alphaVar(colors.gray[200], 0.3)}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: alphaVar(colors.gray[50], 0.3),
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon sx={{ color: colors.gray[400], fontSize: 20 }} />
            <Typography variant="body2" fontWeight={900} sx={{ color: colors.gray[600] }}>
              Email Recipients
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={onAdd}
            disabled={!sendEmail}
            sx={{
              color: colors.orange.base,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.7rem",
              "&:hover": {
                bgcolor: alphaVar(colors.orange.base, 0.08),
              },
              "&.Mui-disabled": {
                color: colors.gray[400],
              },
            }}
          >
            Add Recipient
          </Button>
        </Box>
        <Box
          sx={{
            p: 3,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {recipients.map((recipient) => (
              <Box
                key={recipient.id}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "flex-end" },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: colors.gray[400],
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    type="email"
                    value={recipient.email}
                    onChange={(e) => onUpdate(recipient.id, "email", e.target.value)}
                    placeholder={recipient.id === "3" ? "Enter recipient email..." : ""}
                    variant="outlined"
                    disabled={!sendEmail}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "& fieldset": {
                          borderColor: alphaVar(colors.gray[200], 0.5),
                        },
                        "&:hover fieldset": {
                          borderColor: alphaVar(colors.orange.base, 0.5),
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.orange.base,
                          borderWidth: 1,
                        },
                        "&.Mui-disabled": {
                          bgcolor: alphaVar(colors.gray[50], 0.3),
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ width: { xs: "100%", md: 192 } }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: colors.gray[400],
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Permission Level
                  </Typography>
                  <Select
                    value={recipient.permissionLevel}
                    onChange={(e) => onUpdate(recipient.id, "permissionLevel", e.target.value)}
                    disabled={!sendEmail}
                    size="small"
                    sx={{
                      width: "100%",
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: alphaVar(colors.gray[200], 0.5),
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: alphaVar(colors.orange.base, 0.5),
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: colors.orange.base,
                      },
                      "&.Mui-disabled": {
                        bgcolor: alphaVar(colors.gray[50], 0.3),
                      },
                    }}
                    MenuProps={{
                      disablePortal: false,
                      PaperProps: {
                        sx: {
                          borderRadius: 2,
                          mt: 1,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          zIndex: "99999 !important",
                        },
                        style: {
                          zIndex: 99999,
                        },
                      },
                      style: {
                        zIndex: 99999,
                      },
                      container: typeof document !== "undefined" ? document.body : undefined,
                    }}
                  >
                    <MenuItem value="Can Pay">Can Pay</MenuItem>
                    <MenuItem value="Can View">Can View</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ pb: { xs: 0, md: 0.5 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {recipient.isLocked ? (
                    <LockIcon
                      sx={{
                        color: colors.gray[300],
                        fontSize: 24,
                        opacity: 0.3,
                        cursor: "not-allowed",
                      }}
                    />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => onDelete(recipient.id)}
                      disabled={!sendEmail}
                      sx={{
                        color: colors.gray[300],
                        "&:hover": {
                          color: colors.danger.base,
                          bgcolor: alphaVar(colors.danger.base, 0.08),
                        },
                        "&.Mui-disabled": {
                          color: colors.gray[300],
                          opacity: 0.5,
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {sendEmail ? (
        <Box sx={{ p: 3, pt: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {isPaymentMethodRequired ? (
            <Alert
              icon={<WarningIcon sx={{ fontSize: 18 }} />}
              severity="warning"
              sx={{
                bgcolor: alphaVar(colors.warning[50], 0.5),
                border: `1px solid ${alphaVar(colors.warning[200], 0.3)}`,
                borderRadius: 2,
                boxShadow: "none",
                "& .MuiAlert-icon": {
                  color: colors.warning[600],
                },
                "& .MuiAlert-message": {
                  color: colors.warning[700],
                },
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                You have recipient(s) set to "Can Pay", but no payment method is selected. Please select at least one payment method (e.g. Stripe,
                PayPal, Bank Transfer) or change all recipients to "Can View" to generate this invoice.
              </Typography>
            </Alert>
          ) : hasCanPay ? (
            <Alert
              icon={<InfoIcon sx={{ fontSize: 18 }} />}
              severity="info"
              sx={{
                bgcolor: alphaVar(colors.info[50], 0.5),
                border: `1px solid ${alphaVar(colors.info[200], 0.3)}`,
                borderRadius: 2,
                boxShadow: "none",
                "& .MuiAlert-icon": {
                  color: colors.info[600],
                },
                "& .MuiAlert-message": {
                  color: colors.info[700],
                },
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                'Can Pay' recipients will receive a secure payment link. 'Can View' will receive a read-only PDF/link.
              </Typography>
            </Alert>
          ) : (
            <Alert
              icon={<InfoIcon sx={{ fontSize: 18 }} />}
              severity="info"
              sx={{
                bgcolor: alphaVar(colors.info[50], 0.5),
                border: `1px solid ${alphaVar(colors.info[200], 0.3)}`,
                borderRadius: 2,
                boxShadow: "none",
                "& .MuiAlert-icon": {
                  color: colors.info[600],
                },
                "& .MuiAlert-message": {
                  color: colors.info[700],
                },
              }}
            >
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                All recipients are set to "Can View" (read-only PDF/link). No payment gateway is required.
              </Typography>
            </Alert>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 3, pt: 0 }}>
          <Alert
            icon={<WarningIcon sx={{ fontSize: 18 }} />}
            severity="warning"
            sx={{
              bgcolor: alphaVar(colors.warning[50], 0.5),
              border: `1px solid ${alphaVar(colors.warning[200], 0.3)}`,
              borderRadius: 2,
              boxShadow: "none",
              "& .MuiAlert-icon": {
                color: colors.warning[600],
              },
              "& .MuiAlert-message": {
                color: colors.warning[700],
              },
            }}
          >
            <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
              This invoice will not be sent by email. Recipients can still access it through the invoices widget or by viewing invoices for this
              client or job.
            </Typography>
          </Alert>
        </Box>
      )}
    </Card>
  );
};
